// Import global utils
import { isDefined } from '@automator/shared/utils/is-defined';
import { addParametersToURL } from '@automator/shared/utils/add-parameters-to-url';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __, sprintf } from '@wordpress/i18n';

/**
 * Preprocess Templates to normalize data.
 *
 * @param {Array} templates - The templates to preprocess
 * @param {Array} integrations - The integrations to use
 *
 * @return {Array} The preprocessed templates
 */
const _uapRtlPreprocessTemplates = ( templates, integrations ) => {

	/**
	 * Helper function to map integration IDs to integration objects
	 *
	 * @param {String} id - the integration id
	 * @return {Object} - the integration object
	 */
	const mapIntegration = ( id ) => {
		if ( id === 'GEN' ) {
			return {
				code: 'GEN',
				name: 'General',
			};
		}
		if ( id === -10 || id === -20 ) {
			return {
				code: 'UOA',
				name: id === -10 ? 'Automator Pro' : 'Elite Integrations Addon'
			}
		}
		const integration = integrations.find( integration => integration.id === id );
		return integration ? {
			name: integration.name,
			code: integration.code,
		} : null;
	};

	return templates
		// Filter out any templates without the requirements property.
		.filter( template => Array.isArray( template.requirements ) )
		.map( template => {
			// Map the list of IDs to their corresponding integration objects
			const mappedIntegrations = template.requirements
				.map( mapIntegration )
				// Filter out any null integrations
				.filter( integration => integration !== null );

			return {
				...template,
				id: parseInt( template.id, 10 ), // Ensure id is an integer
				integrations: mappedIntegrations // Replace the integration IDs with the integration
			};
		} );
}

/**
 * Generate a list of templates that are ready to use.
 *
 * @param {Object} library - The library API object.
 *
 * @return {Array<Object>} - The list of templates IDs that are ready to use.
 */
const _uapRtlPreprocessReadyToUseTemplates = ( library ) => {

	/**
	 * Check if the app is connected and ready to use.
	 *
	 * @param {Array<Object>} misc - The miscellaneous data for the integration.
	 * @return {Boolean} - Whether the app is connected and ready to use.
	 */
	const isAppConnected = ( misc ) => {
		return misc.is_app_connected === true && misc.url_settings_page > '';
	};

	/**
	 * Generate an array of integration IDs that are ready to use.
	 *
	 * @return {Array} - An array of integration IDs that are ready to use.
	 */
	const readyIntegrationIds = library.integrations
		.map( integration => {
			if ( integration.is_app === true ) {
				return isAppConnected( integration.miscellaneous ) ? integration.id : null;
			}
			return integration.is_installed === true ? integration.id : null;
		} )
		.filter( id => id !== null );

	// Return an empty array if no integrations are ready to use.
	if ( readyIntegrationIds.length === 0 ) {
		return [];
	}

	// Filter out templates that are not ready to use on the current site.
	return library.templates
		.filter( template => {
			return template.requirements.every( id => readyIntegrationIds.includes( id ) );
		} )
		.map( template => template.id );
}

/**
 * Generate a list of template IDs that require Automator Pro.
 *
 * @param {Array<Object>} templates - The list of templates.
 * @return {Array<Object>} - The list of templates IDs that require Pro.
 */
const _uapRtlPreprocessProOnlyTemplates = ( templates ) => {
	return templates
		.filter( template => template.requirements.includes( -10 ) )
		.map( template => template.id );
}

/**
 * Preprocess categories to normalize data.
 *
 * @param {Array<Object>} categories - Array of category objects to preprocess.
 * @param {Object} library - The library object.
 *
 * @return {Array<Object>} - Preprocessed categories.
 */
const _uapRtlPreprocessCategories = ( categories, library ) => {
	return categories.map( category => {
		// Normalize category.templates to always be an array of integers
		category.templates = category.templates
			.map( id => parseInt( id, 10 ) )
			.filter( Number.isInteger );

		// Add category href
		category.href = category.slug === 'all'
			? library.baseURL
			: sprintf( library.categoryURLsprintf, category.slug );

		return category;
	} );
}

/**
 * Get category object by prop and value.
 *
 * @param {String} prop - category property to search
 * @param {String} term - search term
 * @param {Array} categories - categories to search
 *
 * @return {mixed} category object or false
 */
export const uapRtlFindCategoryByProp = ( prop, term, categories ) => {
	// Normalize the term if the property is 'name'
	const normalizedTerm = prop === 'name' ? term.toLowerCase().trim() : term;
	// Find and return the category that matches the term
	return categories.find( category => {
		const propValue = prop === 'name' ? category[prop].toLowerCase().trim() : category[prop];
		return propValue === normalizedTerm;
	} ) || false;
}

/**
 * Filter recipe templates by category.
 *
 * @param {Object} category - The selected category.
 * @param {Array} templates - The list of templates.
 *
 * @return {Array} - The filtered results.
 */
export const uapRtlFilterTemplatesByCategory = ( category, templates ) => {

	// Return all templates if no category or 'all' category is selected.
	if ( ! category || category.slug === 'all' ) {
		return templates;
	}

	// Filter by IDs.
	const filtered = templates.filter( template =>
		category.templates.includes( template.id )
	);

	return filtered;
}

/**
 * Generate a filter event object.
 *
 * @param {String} type - The type of filter.
 * @param {mixed} filter - The search term or category object.
 * @param {Array} results - The templates found in the search results.
 * @param {number} page - The current page number.
 *
 * @return {CustomEvent} - The filter change event.
 */
export const uapRtlCreateFilterEvent = ( type, filter, results, page = 1 ) => {
	return new CustomEvent( 'uap-rtl-filter-change', {
		detail: {
			filter: {
				type: type,
				filter: filter,
				results: results,
				page: page
			}
		},
		bubbles: true,
		composed: true
	} );
}

/**
 * Manage the URL query params.
 *
 * @param {mixed} filter - The search term or category object.
 * @param {String} type - The type of filter (search, category, integration).
 * @param {number} page - The current page number.
 * @return {void}
 */
export const uapRtlUrlFilterQueryManager = ( filter, type, page ) => {
	/**
	 * Update the URL query params.
	 *
	 * @param {Object} params - The query params to update.
	 */
	const updateURL = params => {
		addParametersToURL(
			window.location.href,
			params,
			{
				setAsURL: true,
				refresh: false
			}
		);
	};

	// Reset the query params
	updateURL( {
		search: '',
		category: '',
		integration: '',
		paged: ''
	} );

	// Set the appropriate query param.
	switch ( type ) {
		case 'search':
			updateURL( {
				search: filter
			} );

			break;

		case 'category':
			if ( filter.slug ) {
				updateURL( {
					category: filter.slug
				} );
			}

			break;

		case 'integration':
			if ( filter.slug ) {
				updateURL( {
					integration: filter.slug
				} );
			}
			break;
	}

	if ( page && page > 1 ) {
		updateURL( {
			paged: page
		} );
	}
}

/**
 * Get the library configuration.
 *
 * @return {Object} The library configuration.
 */
const uapRecipeTemplateLibraryConfig = ( () => {

	if ( ! isDefined( UncannyAutomatorBackend ) ) {
		return {};
	}

	if ( ! isDefined( UncannyAutomatorBackend.templateLibrary ) ) {
		return {};
	}

	const library = UncannyAutomatorBackend.templateLibrary;
	// Add the rest data.
	library.rest = {
		nonce: UncannyAutomatorBackend.ajax.nonce,
		url: `${UncannyAutomatorBackend.rest.url}/template-library`,
		restNonce: UncannyAutomatorBackend.rest.nonce
	};

	// Preprocess the categories.
	if ( library.categories ) {
		library.categories = _uapRtlPreprocessCategories( library.categories, library );
	}

	// Preprocess the templates.
	if ( library.templates ) {
		library.templates = _uapRtlPreprocessTemplates( library.templates, library.integrations );
	}

	// Add the base filters.
	library.baseFilters = [
		{
			name: 'readyToUse',
			label: escapeHTML( __(
				'Ready-to-use',
				'uncanny-automator'
			) ),
			description: escapeHTML( __(
				"Templates I can use now with the plugins on my site and apps I've already connected.",
				'uncanny-automator'
			) ),
			ids: _uapRtlPreprocessReadyToUseTemplates( library )
		},
		{
			name: 'pro',
			label: escapeHTML( __(
				'Automator Pro only',
				'uncanny-automator'
			) ),
			description: escapeHTML( __(
				"Templates that require Uncanny Automator Pro.",
				'uncanny-automator'
			) ),
			ids: _uapRtlPreprocessProOnlyTemplates( library.templates )
		}
	];

	// Set a flag for components.
	library.isReady = true;

	return library;
} )();

export { uapRecipeTemplateLibraryConfig };